<template>
    <div
        v-if="visible"
        class="flex flex-col items-start gap-6 md:flex-row"
    >
        <div class="flex w-full flex-col md:w-1/2">
            <div class="flex w-full flex-1">
                <prime-text
                    id="booking_id"
                    v-model="bookingId"
                    :placeholder="bookingIdName"
                    class="w-full"
                    :pt="{
                        root: {
                            class:
                                bookingIdErrorMessage !== ''
                                    ? 'border-red-500'
                                    : ''
                        }
                    }"
                    :pt-options="{
                        mergeSections: true,
                        mergeProps: true
                    }"
                />

                <div class="flex">
                    <prime-button
                        :loading="addCustomerByBookingIdLoading || isPending"
                        label="Add"
                        @click="addCustomerByBookingId"
                    />

                    <prime-button
                        outlined
                        :loading="addCustomerByBookingIdLoading || isPending"
                        label="Cancel"
                        @click="handleCancel"
                    />
                </div>
            </div>

            <small
                v-if="bookingIdErrorMessage !== ''"
                id="booking_id_help"
                class="text-red-600 pl-2"
                >{{ bookingIdErrorMessage }}</small
            >

            <label
                for="booking_id"
                class="pl-2 text-sm text-gray-400"
                >{{ bookingIdName }}</label
            >
        </div>

        <div class="text-base md:w-1/2">
            Your friend or family can find their {{ bookingIdName }} on their
            profile summary. Suggested Friends and Family can be found
            <nuxt-link :to="profileFriendsAndFamilyLink()"
                ><prime-button
                    link
                    class="!p-0"
                    label="here" /></nuxt-link
            >.
        </div>
    </div>
    <confirm-dialog
        group="enrollmentAddByBookingId"
        :pt="{
            root: {
                class: '!w-[400px]'
            },
            footer: {
                class: 'space-x-4'
            },
            rejectButton: {
                outlined: true
            }
        }"
        :pt-options="{
            mergeProps: true
        }"
    />
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const EnrollmentAddByBookingIdAddSavedParticipantResultFragment =
    graphql(`
        fragment EnrollmentAddByBookingIdAddSavedParticipantResultFragment on AddSavedParticipantResult {
            records {
                id
                commaName
                firstName
                middleName
                lastName
                dateOfBirth
            }
        }
    `)
</script>

<script setup lang="ts">
import PrimeText from 'primevue/inputtext'
import type { EnrollmentInputExistingEnrollment } from '~/composables/enrollment-input'
import { computed, ref } from 'vue'
import PrimeButton from 'primevue/button'
import { useAddSavedParticipantMutation } from '~/composables/mutations/use-add-saved-participant-mutation'
import { useQueryClient } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { getFragmentData } from '~/resources/graphql'
import { useBookingIdName } from '~/composables/use-org'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmDialog from 'primevue/confirmdialog'
import { useFindCustomerByBookingId } from '~/components/PurchaseContract/CustomerPicker/find-customer-by-booking-id'
import { useRouteLink } from '~/composables/routing'
import NuxtLink from '#app/components/nuxt-link'

const props = defineProps<{
    visible: boolean
    showErrors: boolean
}>()

const emit = defineEmits<{
    (e: 'addEnrollment', payload: EnrollmentInputExistingEnrollment): void
    (e: 'update:visible', payload: boolean): void
    (e: 'back'): void
}>()

const bookingIdName = useBookingIdName()
const bookingId = ref('')
const bookingIdHelpText = ref(
    'Please enter ' + bookingIdName.value + ' and click Add'
)
const showBookingIdErrors = ref(false)
const bookingIdErrorMessage = computed(() => {
    if (showBookingIdErrors.value || props.showErrors) {
        return bookingIdHelpText.value
    }
    return ''
})

const { profileFriendsAndFamilyLink } = useRouteLink()

const addCustomerByBookingIdLoading = ref(false)
const { mutateAsync: addSavedParticipant, isPending } =
    useAddSavedParticipantMutation()
const queryClient = useQueryClient()
const toast = useToast()
const confirm = useConfirm()
const findCustomerByBookingId = useFindCustomerByBookingId()
async function addCustomerByBookingId() {
    if (bookingId.value === '') {
        showBookingIdErrors.value = true
        bookingIdHelpText.value =
            'Please enter ' + bookingIdName.value + ' and click Add'
        return
    }

    addCustomerByBookingIdLoading.value = true
    // Find customer
    const data = await findCustomerByBookingId(bookingId.value)

    // If no customer found
    if (data === null) {
        showBookingIdErrors.value = true
        bookingIdHelpText.value =
            'No customer with this ' + bookingIdName.value + ' found'
    } else {
        confirm.require({
            group: 'enrollmentAddByBookingId',
            message: 'Add ' + data.commaName + ' to your Family and Friends?',
            header: 'Family and Friends',
            rejectClass: 'bg-white !text-primary-500 hover:bg-white',
            accept: async () => {
                await addSavedParticipant(
                    { customerBookingIds: [bookingId.value] },
                    {
                        onSuccess: async data => {
                            // This should be covered by the first if-clause above
                            if (data.__typename === 'CustomerNotFound') {
                                throw new Error()
                            }

                            // Customer already saved participant
                            if (data.__typename === 'CustomerAlreadySaved') {
                                showBookingIdErrors.value = true
                                bookingIdHelpText.value =
                                    'Customer is already in your Family and Friends'
                                return
                            }

                            // Else successful result
                            const resultData = getFragmentData(
                                EnrollmentAddByBookingIdAddSavedParticipantResultFragment,
                                data
                            )

                            // Invalidate the saved participants query from the parent so option list refreshes
                            await queryClient.invalidateQueries({
                                predicate: query =>
                                    query.queryKey.includes(
                                        'ProfileCustomersQuery'
                                    )
                            })

                            if (resultData.records.length === 0) {
                                throw new Error()
                            }

                            const record = resultData.records[0]

                            // Select the newly added participant
                            // End result is having the new customer added as a saved participant and selected
                            if (record.dateOfBirth === null) {
                                throw new Error()
                            }
                            emit('addEnrollment', {
                                type: 'existing',
                                id: record.id,
                                commaName: record.commaName,
                                firstName: record.firstName,
                                middleName: record.middleName,
                                lastName: record.lastName,
                                dateOfBirth: record.dateOfBirth
                            })

                            toast.add({
                                severity: 'success',
                                summary: record.commaName + ' Added',
                                life: 5000,
                                closable: false
                            })

                            emit('update:visible', false)
                            bookingId.value = ''
                            bookingIdHelpText.value =
                                'Please enter ' +
                                bookingIdName.value +
                                ' and click Add'
                            showBookingIdErrors.value = false
                        }
                    }
                )
            }
        })
    }
    addCustomerByBookingIdLoading.value = false
}

function handleCancel() {
    emit('back')
}
</script>
